/**
 * SalesDashboard.tsx — Sales / Dashboard, mockup-parity layout (§10, P10-1..3).
 *
 * Mockup structure (klaxiora-crm-mockup.html:356-441): greeting head + New
 * Deal button → four icon-less KPI cards → [Revenue | Deals by stage] →
 * [Top open deals | Recent activity]. Cards are VALUE-ONLY (P10-2/R6-1: no
 * fabricated deltas; a future delta line slots under each value without
 * re-layout). The pre-parity extras (weighted pipeline, avg deal size,
 * all-time closed won, the right-rail summary) render on Overview home and
 * Reports — removed from THIS page only (P10-1).
 *
 * Live data: KPIs + revenue from /v1/sales/reports via the summary, funnel
 * from the server's zero-filled 5-row spine (NEVER /stats dealsByStage,
 * which drops empty stages), deals + activity from their real lists.
 *
 * SB-12: the four KPI cards and the funnel rows drill in-page to the deal
 * rows behind each figure, through the SHARED ReportsDrillView +
 * drillShortfall (SalesReports.tsx / reportsDrill.ts) — one drill component,
 * one reconciliation path, the R7-1..6 state-swap shape. Win rate is the
 * server's closed-cohort winRateClosedPct (definition A), NOT the legacy
 * client-derived dealsWinRate.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical design system).
 */
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { DealStageBadge, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchSalesSummary, SLUG_TO_STAGE } from '../../api'
import { currencySymbol } from '../../../../lib/money'
import { NewDealDrawer } from './SalesPipeline'
import { topOpenDeals, closedThisMonthPence, dealsByCompany, companyDeals } from './dashboardView'
import { ReportsDrillView } from './SalesReports'
import { type ReportsDrill } from './reportsDrill'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { accent, ink, edge, shadow, stageBar, surface } from '../../../../theme/tokens'

// "2026-03" → "Mar" — fixed table, no locale/timezone parsing on a bare key.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Takes integer PENCE (the summary/deal money unit since the D9 switch).
// Per-row calls pass the deal's ORIGINAL currency (0111: non-GBP deals
// exist once captured); aggregate figures stay £ — they sum the
// GBP-comparable leg.
export function fmtMoney(pence: number, currency = 'GBP') {
  // SB-10: same swap as fmtCompact — shared symbol rule, spacing and
  // compaction byte-identical.
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  const n = pence / 100
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}k`
  return `${sym}${n.toFixed(0)}`
}

/**
 * The k-metric widget for the Sales pages (S-Tier slice 3) — the OpsMetric
 * precedent verbatim: it lives WITH the Sales pages and is markup-level reuse
 * of the k-* contract, so DashboardShared's MetricCard and its non-Sales
 * consumers stay untouched. Value-only with an optional note line — Sales has
 * no honest period-over-period delta (R6-1), so no delta slot renders.
 */
export function SalesMetric({ label, value, note, onClick }: {
  label: string; value: string | number; note?: string
  /** SB-12: a card with a drill behind it renders as a real <button>
   *  (keyboard-reachable). Resting render is class-identical to the div —
   *  .k-metric overrides every button UA style, so the pixel pin holds
   *  (the R7-5 doctrine: affordances are hover/focus-only). */
  onClick?: () => void
}) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className="k-metric w-full text-left cursor-pointer focus:outline-none">
        {inner}
      </button>
    )
  }
  return <div className="k-metric">{inner}</div>
}

export function SalesDashboard({ onNavigate, onSelectDeal, sessionUser }: {
  onNavigate: (page: string) => void
  onSelectDeal?: (id: string) => void
  /** The session user's display name (else email) from the shell's /v1/me
   *  state; null until loaded — the greeting renders nameless. */
  sessionUser?: string | null
}) {
  // Bumped after a drawer create so useAsync re-runs and the new deal shows.
  const [refreshKey, setRefreshKey] = useState(0)
  const [showNewDeal, setShowNewDeal] = useState(false)
  // SB-12: in-page drill state (the R7-1..6 shape — no URL router, no
  // cross-page filter navigation). Rendered through the SHARED
  // ReportsDrillView; this page adds no drill machinery of its own.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  const { data, loading, error } = useAsync(fetchSalesSummary, [refreshKey])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)}
      onSelectDeal={onSelectDeal} backLabel="Back to dashboard"
      // SB-13: the company drill filters the SAME list its rollup row was
      // computed from — never a refetch, so drill and number cannot diverge.
      sourceDeals={drill.kind === 'company' ? companyDeals(data!.deals, drill.name) : undefined} />
  }

  const { summary, deals, activities } = data!
  const openDeals = topOpenDeals(deals)
  const companyRollup = dealsByCompany(deals)
  const recentActivities = activities.slice(0, 5)
  const maxFunnel = Math.max(1, ...summary.funnel.map(f => f.count))
  // Mockup greets by first name ("Good morning, Dylan"); an email session
  // user greets by the full address rather than a fabricated split.
  const firstName = sessionUser && !sessionUser.includes('@')
    ? sessionUser.trim().split(/\s+/)[0]
    : sessionUser

  // The mockup's four cards, value-only (P10-2), each clickable to the rows
  // behind its figure (SB-12). Win rate is the SERVER closed-cohort rate
  // (won ÷ won+lost, whole population) — absent (pre-SB-12 backend, or no
  // closed deals yet) renders an em dash, never a fake 0%. Open-pipeline and
  // Open-deals share ONE composite open drill: two figures, same population.
  const openDrill: ReportsDrill = { kind: 'open', openDeals: summary.openDeals, pipelinePence: summary.totalPipeline }
  const lastBucket = summary.monthlyRevenue.length > 0
    ? summary.monthlyRevenue[summary.monthlyRevenue.length - 1] : null
  const KPIS: { label: string; value: string; drill?: ReportsDrill }[] = [
    { label: 'Open pipeline value', value: fmtMoney(summary.totalPipeline), drill: openDrill },
    { label: 'Open deals',          value: String(summary.openDeals), drill: openDrill },
    { label: 'Win rate',
      value: summary.winRateClosedPct != null ? `${Math.round(summary.winRateClosedPct)}%` : '—',
      drill: summary.wonCount != null && summary.lostCount != null
        ? { kind: 'closed', wonCount: summary.wonCount, lostCount: summary.lostCount } : undefined },
    { label: 'Closed this month',   value: fmtMoney(closedThisMonthPence(summary.monthlyRevenue)),
      drill: lastBucket ? { kind: 'month', month: lastBucket.month, wonPence: lastBucket.won } : undefined },
  ]

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Greeting head (mockup :357-366). "Good morning" is static, mockup-
          verbatim — a clock-derived greeting would make the pixel pin vary
          by time of day. */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-section-title font-bold text-ink-primary">
            Good morning{firstName ? `, ${firstName}` : ''}
          </h2>
          <p className="text-13 text-ink-muted mt-1">Here's how the sales pipeline is tracking this month.</p>
        </div>
        <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNewDeal(true)}>
          <Plus size={14} /> New Deal
        </button>
      </div>

      {/* Four KPI cards (mockup :368-388) — .k-metric, value-only (P10-2);
          no delta/dot: Sales has no honest period-over-period figure.
          Each drills to the rows behind its number (SB-12); a card whose
          drill context is absent stays a plain div. */}
      <div className="grid grid-cols-4 gap-3">
        {KPIS.map(k => (
          <SalesMetric key={k.label} label={k.label} value={k.value}
            onClick={k.drill ? () => setDrill(k.drill!) : undefined} />
        ))}
      </div>

      {/* Row 1: Revenue | Deals by stage (mockup :390-414) */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        {/* Revenue by month (closed-won value, /v1/sales/reports) — honest
            labels kept over the mockup's "Revenue trend" (P10-5, §6.B pin). */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Revenue by month</div>
              <div className="k-card-sub">Closed-won value, last 6 months</div>
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary.monthlyRevenue.map(m => ({ ...m, label: MONTH_ABBR[Number(m.month.slice(5, 7)) - 1] ?? m.month }))}>
                <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} tickFormatter={v => fmtMoney(v)} />
                <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} formatter={(v: number) => fmtMoney(v)} cursor={{ fill: surface['row-hover'] }} />
                <Bar dataKey="won" name="Won" fill={accent.DEFAULT} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Deals by stage — the server's zero-filled 5-row spine
            (summary.funnel), so every stage renders even at 0. Rows reuse the
            Reports funnel structure and, since SB-12, its DRILL too — real
            buttons to the stage's deal list (the pre-SB-12 inertness is
            history; resting classes unchanged, so the pixel pin holds). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Deals by stage</div>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {summary.funnel.map((f, i) => (
              <button key={f.stage} type="button"
                onClick={() => setDrill({ kind: 'stage', stage: f.stage, label: SLUG_TO_STAGE[f.stage] ?? f.stage, count: f.count })}
                className="flex items-center gap-3 w-full text-left cursor-pointer rounded-control hover:bg-surface-nav-hover focus:outline-none">
                <div className="w-24 flex-shrink-0 text-12 text-ink-secondary">{SLUG_TO_STAGE[f.stage] ?? f.stage}</div>
                <div className="flex-1 h-2 rounded-full bg-surface-control overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(f.count / maxFunnel) * 100}%`, background: stageBar[i % stageBar.length] }} />
                </div>
                <div className="w-8 flex-shrink-0 text-right text-13 font-semibold text-ink-primary">{f.count}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Top open deals | Recent activity (mockup :416-440) */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        {/* Top open deals — open stages by value, capped rows; the input list
            is the limit-200 newest-first call (see dashboardView.ts). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Top open deals</div>
          </div>
          {openDeals.length === 0 ? (
            <EmptyState message="No open deals" />
          ) : (
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Deal</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Stage</th>
                  <th className="text-left">Value</th>
                </tr>
              </thead>
              <tbody>
                {openDeals.map(d => (
                  <tr key={d.id} className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                    onClick={() => onSelectDeal?.(d.id)}>
                    <td className="font-medium text-ink-primary">{d.name}</td>
                    <td className="text-13 text-ink-body">{d.company}</td>
                    <td><DealStageBadge stage={d.stage} /></td>
                    <td className="font-semibold text-ink-primary">{fmtMoney(d.amount, d.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent activity — real /v1/sales/activities; an empty tenant shows
            the true empty state (never faked). */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Activity</div>
            <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
              onClick={() => onNavigate('sales.activities')}>See All</button>
          </div>
          <div className="divide-y divide-edge-row-faint">
            {recentActivities.length === 0 ? (
              <div className="px-4 py-6 text-center text-12 text-ink-muted">No recent activity</div>
            ) : (
              recentActivities.map(a => (
                <div key={a.id} className="px-4 py-3 hover:bg-surface-row-hover transition-colors">
                  <div className="flex items-start gap-2">
                    <div className={`mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full ${a.type === 'email' ? 'bg-accent' : a.type === 'call' ? 'bg-success' : a.type === 'meeting' ? 'bg-info' : 'bg-warning'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-12 text-ink-body leading-snug">
                        <span className="font-semibold text-ink-primary">{a.contact}</span>
                        {a.description ? ` — ${a.description}` : ''}
                      </p>
                      <div className="text-overline text-ink-faint mt-1">{a.time}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Deals by company (SB-2 item 5) — client-side rollup over the
          page's own deals fetch (recon decision: no endpoint at this scale).
          String-grouped: company is free text on the deal, so case/spelling
          variants split rows — the Top-performers v1 limit, worn openly. */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Deals by company</div>
            <div className="k-card-sub">All deals, grouped by the company on the deal</div>
          </div>
        </div>
        {companyRollup.length === 0 ? (
          <EmptyState message="No deals yet" />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Company</th>
                <th className="text-right">Deals</th>
                <th className="text-right">Won</th>
                <th className="text-right">Open</th>
                <th className="text-right">Open pipeline</th>
                <th className="text-right">Won value</th>
              </tr>
            </thead>
            <tbody>
              {companyRollup.slice(0, 8).map(c => {
                // SB-13: rows drill to the company's deals — the Top-
                // performers tr pattern (a <button> cannot legally wrap a
                // table row); keyboard-reachable, resting classes carry
                // only hover/cursor affordances so the pixel pin holds.
                const go = () => setDrill({
                  kind: 'company', name: c.company, total: c.total, won: c.won,
                  open: c.open, pipelinePence: c.pipelinePence, wonPence: c.wonPence,
                })
                return (
                  <tr key={c.company} tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="cursor-pointer hover:bg-surface-row-hover transition-colors focus:outline-none">
                    <td className="font-medium text-ink-primary">{c.company}</td>
                    <td className="text-right text-13 text-ink-body">{c.total}</td>
                    <td className="text-right text-13 text-ink-body">{c.won}</td>
                    <td className="text-right text-13 text-ink-body">{c.open}</td>
                    <td className="text-right font-semibold text-ink-primary">{fmtMoney(c.pipelinePence)}</td>
                    <td className="text-right text-13 text-ink-body">{fmtMoney(c.wonPence)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {companyRollup.length > 8 && (
          <div className="px-4 py-2 text-12 text-ink-muted">and {companyRollup.length - 8} more companies</div>
        )}
      </div>

      {showNewDeal && (
        <NewDealDrawer
          initialStage="New Lead"
          onClose={() => setShowNewDeal(false)}
          onCreated={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  )
}
